import React, { useState } from 'react';
import { Form, Button, Card, Row, Col } from 'react-bootstrap';
import { User } from 'firebase/auth';
import { db } from '../firebase';
import { collection, addDoc } from 'firebase/firestore';

interface HraCalculatorProps {
  currentUser: User | null;
}

const HraCalculator: React.FC<HraCalculatorProps> = ({ currentUser }) => {
  const [basicSalary, setBasicSalary] = useState<number>(0);
  const [hraReceived, setHraReceived] = useState<number>(0);
  const [rentPaid, setRentPaid] = useState<number>(0);
  const [isMetroCity, setIsMetroCity] = useState<boolean>(false);
  const [exemptedHra, setExemptedHra] = useState<number | null>(null);

  const calculateHraExemption = () => {
    const actualHra = hraReceived;
    const excessRentPaid = rentPaid - (basicSalary * 0.10);
    const cityAllowance = isMetroCity ? basicSalary * 0.50 : basicSalary * 0.40;

    const exemption = Math.min(actualHra, excessRentPaid, cityAllowance);
    
    setExemptedHra(Math.max(0, exemption));
  };

  return (
    <Card className="p-4 shadow-sm">
      <h4 className="mb-4 text-center">HRA Exemption Calculator</h4>
      <Form>
        <Form.Group as={Row} className="mb-3">
          <Form.Label column sm={5}>Basic Salary (Annual)</Form.Label>
          <Col sm={7}>
            <Form.Control
              type="number"
              value={basicSalary}
              onChange={(e) => setBasicSalary(parseFloat(e.target.value))}
            />
          </Col>
        </Form.Group>

        <Form.Group as={Row} className="mb-3">
          <Form.Label column sm={5}>HRA Received (Annual)</Form.Label>
          <Col sm={7}>
            <Form.Control
              type="number"
              value={hraReceived}
              onChange={(e) => setHraReceived(parseFloat(e.target.value))}
            />
          </Col>
        </Form.Group>

        <Form.Group as={Row} className="mb-3">
          <Form.Label column sm={5}>Rent Paid (Annual)</Form.Label>
          <Col sm={7}>
            <Form.Control
              type="number"
              value={rentPaid}
              onChange={(e) => setRentPaid(parseFloat(e.target.value))}
            />
          </Col>
        </Form.Group>

        <Form.Group as={Row} className="mb-3">
          <Form.Label column sm={5}>Living in Metro City?</Form.Label>
          <Col sm={7}>
            <Form.Check
              type="checkbox"
              checked={isMetroCity}
              onChange={(e) => setIsMetroCity(e.target.checked)}
              label="(Delhi, Mumbai, Chennai, Kolkata)"
            />
          </Col>
        </Form.Group>

        <div className="text-center">
          <Button variant="primary" onClick={calculateHraExemption}>
            Calculate HRA Exemption
          </Button>
          {currentUser && exemptedHra !== null && (
            <Button variant="secondary" onClick={async () => {
              try {
                await addDoc(collection(db, `users/${currentUser.uid}/savedCalculations`), {
                  calculatorType: 'hra',
                  basicSalary,
                  hraReceived,
                  rentPaid,
                  isMetroCity,
                  exemptedHra,
                  timestamp: new Date(),
                });
                alert('HRA calculation saved!');
              } catch (e) {
                console.error('Error saving document: ', e);
                alert('Error saving calculation.');
              }
            }} className="ms-2">
              Save Calculation
            </Button>
          )}
        </div>

        {exemptedHra !== null && (
          <div className="mt-4 text-center">
            <h5>Exempted HRA Amount: ₹{exemptedHra.toFixed(2)}</h5>
            <p className="text-muted">Taxable HRA: ₹{(Math.max(0, hraReceived - exemptedHra)).toFixed(2)}</p>
          </div>
        )}
      </Form>
    </Card>
  );
};

export default HraCalculator;
